<script setup>
import { ref, onMounted, computed } from 'vue';
import { useI18n } from 'vue-i18n';

const { t } = useI18n();
// Bound (not literal) so Vite treats this as a runtime string,
// not a build-time module import. Absolute /public paths fail
// through Rollup otherwise.
const iconUrl = '/android-icon-96x96.png';
const DISMISS_KEY = 'synkra-pwa-install-dismissed-at';
const DISMISS_COOLDOWN_DAYS = 30;

const deferredPrompt = ref(null);
const visible = ref(false);

const isStandalone = () =>
  window.matchMedia('(display-mode: standalone)').matches ||
  window.navigator.standalone === true;

const isRecentlyDismissed = () => {
  const raw = localStorage.getItem(DISMISS_KEY);
  if (!raw) return false;
  const dismissedAt = Number(raw);
  if (!dismissedAt) return false;
  const ageDays = (Date.now() - dismissedAt) / (1000 * 60 * 60 * 24);
  return ageDays < DISMISS_COOLDOWN_DAYS;
};

const showBanner = () => {
  if (isStandalone()) return;
  if (isRecentlyDismissed()) return;
  if (!deferredPrompt.value) return;
  visible.value = true;
};

const dismiss = () => {
  visible.value = false;
  localStorage.setItem(DISMISS_KEY, String(Date.now()));
};

const install = async () => {
  if (!deferredPrompt.value) return;
  deferredPrompt.value.prompt();
  const { outcome } = await deferredPrompt.value.userChoice;
  deferredPrompt.value = null;
  if (outcome === 'accepted') {
    visible.value = false;
  } else {
    dismiss();
  }
};

onMounted(() => {
  window.addEventListener('beforeinstallprompt', event => {
    event.preventDefault();
    deferredPrompt.value = event;
    // Give the app a beat to settle before nudging
    setTimeout(showBanner, 3000);
  });

  window.addEventListener('appinstalled', () => {
    visible.value = false;
    localStorage.removeItem(DISMISS_KEY);
  });
});

const bannerText = computed(() => t('PWA_INSTALL.DESCRIPTION'));
</script>

<template>
  <div
    v-if="visible"
    class="fixed bottom-4 right-4 z-50 max-w-sm rounded-xl border border-n-strong bg-n-solid-1 shadow-lg p-4 flex flex-col gap-3"
    role="dialog"
  >
    <div class="flex items-start gap-3">
      <img
        :src="iconUrl"
        alt="Synkra"
        class="size-10 rounded-lg flex-shrink-0"
      />
      <div class="flex-1 min-w-0">
        <p class="text-sm font-semibold text-n-slate-12">
          {{ t('PWA_INSTALL.TITLE') }}
        </p>
        <p class="text-xs text-n-slate-11 mt-1">
          {{ bannerText }}
        </p>
      </div>
      <button
        type="button"
        class="text-n-slate-10 hover:text-n-slate-12 text-lg leading-none"
        aria-label="Dismiss"
        @click="dismiss"
      >
        ×
      </button>
    </div>
    <div class="flex gap-2 justify-end">
      <button
        type="button"
        class="px-3 py-1.5 text-xs text-n-slate-11 hover:text-n-slate-12"
        @click="dismiss"
      >
        {{ t('PWA_INSTALL.DISMISS') }}
      </button>
      <button
        type="button"
        class="px-3 py-1.5 text-xs font-medium rounded-lg bg-n-brand text-white hover:opacity-90"
        @click="install"
      >
        {{ t('PWA_INSTALL.BUTTON') }}
      </button>
    </div>
  </div>
</template>
